/* eslint-disable react/no-children-prop */
 
 

import { BreadcrumbComponent, Button, CIPageHeader, CIRouteguard, CIStatusCapsule, ConfirmationPopup, Loader, Modal, MyIcon, useToastMessage, useUserPermission } from 'ccs-ui-components';
import { debounce, isEqual } from 'lodash';
import { useEffect, useReducer, useRef, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { SideModal } from '../../../../common/components/UI/SideModal/SideModal';
import { requiredAuthPermission } from '../../../../common/constants/UserPermissionConstants';
import {
  buildJourneyDesignerFlowViewUrl,
  clearJourneyDesignerReturnContext,
  getJourneyDesignerReturnContext,
  type JourneyDesignerServiceReturnLocationState
} from '../../../../common/utilities/JourneyDesignerReturnContext';
import { RouteObject } from '../../../../common/types/interfaces';
import CommentDialog from '../../../_components/comment-dialog/CommentDialog';
import CommentHistoryModal from '../../../_components/comment-history-modal/CommentHistoryModal';
import CreateServiceSideNav from '../../../_components/create-service-sidenav/CreateServiceSideNav';
import InfoMessage from '../../../_components/info-messsage/InfoMessage';
import WarningMessage from '../../../_components/warning-message/WarningMessage';
import { DEBOUNCE_DELAY, SERVICE_MODE, SERVICE_TYPE_KEY, STATUS_TYPES, STRING_CONSTANTS, URL_PATH, VIEW_SERVICE_URL_PATH, cloneConstant } from '../../../constants';
import { useCreateService, useImportFileData, useSaveServiceAsDraft, useUpdateServiceStatus } from '../../../hooks';
import { IService, IServiceDetailsUIModel, IServiceOperationRequestModel, NavBarState } from '../../../models/index';
import { AppDispatch, useSelector } from '../../../store/AppStore';
import { getDecryptedData } from '../../../store/_api/services/DecryptServiceDetails';
import { clearServiceData } from '../../../store/actions/sagaActions/services/CreateServiceSagaActions';
import { getServiceRequest } from '../../../store/actions/sagaActions/services/ServiceNameSagaAction';
import { clearUpdateServiceData } from '../../../store/actions/sagaActions/services/UpdateServiceSagaActions';
import { addEgressEventMappingList, clearEgressEventMapping } from '../../../store/reducer/services/EgressEventMappingReducer';
import { addAllException, clearAllExceptions, clearInvalidExceptions } from '../../../store/reducer/services/ExceptionsReducer';
import { addIngressEventMappingList, clearIngressEventMapping } from '../../../store/reducer/services/IngressEventMappingReducer';
import { serviceDetailsComplete } from '../../../store/reducer/services/NavBarReducer';
import { ISaveAsDraftInitialState, addAllEgressEventForDraft, addAllExceptionForDraft, addAllIngressEventForDraft, addSaveAsDraft, addServiceOperationForDraft } from '../../../store/reducer/services/SaveAsDraftReducer';
import { addServiceDetails } from '../../../store/reducer/services/ServiceDetailsReducer';
import { getServiceNameSuccess } from '../../../store/reducer/services/ServiceNameReducer';
import { setIsEditService } from '../../../store/reducer/services/ServiceOperationMonitorReducer';
import { addAllServiceOperations, clearServiceOperations } from '../../../store/reducer/services/ServiceOperationReducer';
import { callGetServiceApi } from '../../../store/sagas/services/ServiceNameValidationSaga';
import { DEPENDENCY_ACTIONS, dependencyGraphInitialState, getDependencyGraphStateReducer } from '../../../utilities/RegistryDependancyManager';
import { constructServiceDetailsForStore, getClonedDraftServiceDetails, getCreateServiceURLArray, getCreateViewServiceTitle, getEditServiceURLArray, getEgressEventListForUI, getExceptionsForUI, getIngressEventListForUI, getServiceOperationListForUI, getStickyStyle } from '../../../utilities/services/ServiceDetailsUtils';
import { getModeFromUrl, getServiceNameFromUrl, getServicePath, getVersionFromUrl } from '../../../utilities/services/ServiceListUtils';
import { shouldShowNavigationAwayPopup } from '../../../utilities/services/ServiceWrapperUtil';
import classes from './ServiceWrapper.module.scss';
import showImportNotification from './helper/ImportNotification';
import ServiceDetailsForm, { ServiceDetailsFormRef } from './service-details/ServiceDetailsForm';
import { InvalidExceptionModal, ServiceExceptions } from './service-exceptions';
import { ServiceOperationWrapper } from './service-operation';
import ServicePatchButtons from './service-patch-buttons/ServicePatchButtons';

const ServiceWrapper = () => {
  const serviceNameFieldRef = useRef<ServiceDetailsFormRef>(null);
  /* Local states */
  const [, setPrevUrl] = useState<string>('');
  const [btnDraftEnabled, setBtnDraftEnabled] = useState<boolean>(false);
  const [btnInvalid, setBtnInvalid] = useState<boolean>(false);
  const [exceptionInvalid, setExceptionInvalid] = useState<boolean>(false);
  const [isEdit, setIsEdit] = useState(false);
  const [showCommentDialog, setShowCommentDialog] = useState(false);
  const [returnedStatusOpen, setReturnedStatusOpen] = useState<boolean>(false);
  const [clonedServiceDialog, setClonedServiceDialog] = useState<boolean>(false);
  const [viewStatus, setViewStatus] = useState(false);
  const [serviceVersion, setServiceVersion] = useState<string | undefined>();
  const [isSticky, setIsSticky] = useState(false);
  const [isCreateClicked, setIsCreateClicked] = useState(false);
  const [isDecryptionLoading, setIsDecryptionLoading] = useState(false);
  const [isPatchUpdateCalled, setIsPatchUpdateCalled] = useState(false);
  const { serviceDetails, isLoading, serviceErrorMessage } = useSelector((state) => state.getServiceNameReducer);
  const currentActiveService = serviceDetails[0];
  /* Local Variables */
  const serviceUrlStatus = currentActiveService?.status ?? '';
  const serviceMode = getModeFromUrl();
  const serviceNameFromURL = getServiceNameFromUrl();
  const version = getVersionFromUrl();
  const servicePath = getServicePath(serviceNameFromURL, serviceMode, version);
  const createServiceUrlArray = getCreateServiceURLArray();
  const editServiceUrlsArray = getEditServiceURLArray(serviceNameFromURL, version);
  const { pathname } = window.location;
  // Hooks
  const { success, failure, warning, renderToast } = useToastMessage();
  const { isCreateServiceLoading, callCreateService } = useCreateService(success, failure, warning, setViewStatus, setIsEdit, viewStatus);
  const { updateServiceStatus, isServicePatchLoading } = useUpdateServiceStatus(success, failure, warning, serviceNameFromURL, version, setIsPatchUpdateCalled);
  const { updateServiceToDraftState, isUpdateServiceLoading, isUpdateServiceSuccess, setIsUpdateServiceSuccess, isActiveUpdate } = useSaveServiceAsDraft(success, failure, warning, setViewStatus, setIsEdit);
  const { importState, updateImportFileData, closeImportNotification } = useImportFileData(failure);
  const [dependencyState, dispatchDependencyState] = useReducer(getDependencyGraphStateReducer, dependencyGraphInitialState);
  const location = useLocation();
  const cloneServiceName = location.state?.cloneServiceName ?? '';
  const registryVersion = location.state?.version ?? '';
  const dispatch = useDispatch<AppDispatch>();
  const history = useNavigate();
  const { t: translate } = useTranslation();
  const { permissionObject } = useUserPermission();
  /* Get Data from the store */
  const { serviceOperations } = useSelector((state) => state.serviceOperationReducer);
  const isViewOrEditService = viewStatus || isEdit;

  const { ingressEventMappingList } = useSelector((state) => state.ingressEventMappingReducer);
  const { serviceDetails: navBarServiceDetails, serviceExceptions: navBarServiceExceptions, serviceOperations: navBarServiceOperation } = useSelector((state) => state.navBarStateReducer);
  const serviceCommentHistoryCheck = serviceDetails[0]?.serviceStatusHistory;
  const { egressEventMappingList } = useSelector((state) => state.egressEventMappingReducer);
  const { exceptions, importedInvalidExceptions } = useSelector((state) => state.exceptionReducer);
  const serviceDetailsData = useSelector((state) => state.serviceDetailsReducer);
  const serviceDraftData = useSelector((state) => state.saveAsDraftReducer);
  const { success: createServiceSuccess, errorMessage: createServiceErrorMessage, serviceData: createServiceData } = useSelector((state) => state.serviceReducer);
  const { serviceData } = useSelector((state) => state.updateServiceReducer);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [isServiceNameExistWarning, setIsServiceNameExistWarning] = useState(false);

  const closeUploadNotification = () => {
    closeImportNotification();
  };

  /**
   * @method updateViewMode
   * @description Update the view mode
   */
  const updateViewMode = () => {
    setIsEdit(false);
  };

  /**
   * @method updateUIToEditMode
   * @description change service mode to edit mode
   */
  const updateUIToEditMode = () => {
    setIsEdit(true);
    dispatch(setIsEditService(true));
    const path = location.pathname;
    history(`${path.substring(0, path.lastIndexOf('/'))}/${SERVICE_MODE.EDIT}`, {
      state: location.state
    });
    setIsUpdateServiceSuccess(false);
  };

  /**
   * @method enableEditService
   * @description Enables the edit mode
   */
  const enableEditService = () => {
    setReturnedStatusOpen(false);
    if (serviceUrlStatus.toLocaleUpperCase() === STATUS_TYPES.ACTIVE) {
      // check for all status except inactive
      const draftService = callGetServiceApi(
        serviceNameFromURL,
        version
      );
      draftService.then((resp) => {
        if (resp?.statusCode === STRING_CONSTANTS.STATUS_CODE_SUCCESS_DATA) {
          if (resp?.serviceRecords && resp?.serviceRecords?.length > 1) {
            failure(
              translate('toast_component.lbl_error'),
              translate(
                'service_wrapper_component.msg_draft_service_exists_failed_body'
              )
            );
          } else {
            updateUIToEditMode();
          }
        }
      }).catch(() => { });
    } else {
      updateUIToEditMode();
    }
  };

  const callCreateServiceApi = () => {
    callCreateService();
  };

  const backToServiceBtnHandler = () => {
    const jdContext = getJourneyDesignerReturnContext();
    if (jdContext) {
      clearJourneyDesignerReturnContext();
      const returnState: JourneyDesignerServiceReturnLocationState = {
        jdServiceReturn: true,
        jdResume: jdContext.serviceReturnResume ?? { kind: 'none' },
        jdResumeSidebar: jdContext.resumeSidebar ?? 'ALL_SERVICES',
      };
      history(buildJourneyDesignerFlowViewUrl(jdContext, 'jdReturn=1'), { state: returnState });
      return;
    }
    const queryParam = location.state;
    if (queryParam?.from) {
      history(`${queryParam.from}`);
    } else if (queryParam?.path?.path) {
      history(`${URL_PATH.SERVICE_HOME}${queryParam.path.path}`);
    } else if (queryParam?.path) {
      history(`${URL_PATH.SERVICE_HOME}${queryParam.path}`);
    } else {
      history(`${URL_PATH.SERVICE_HOME}`);
    }
  };

  const getExceptionalUrlPath = () => {
    if (serviceNameFromURL) {
      return editServiceUrlsArray;
    }
    return createServiceUrlArray;
  };

  const callServiceDetailsApi = debounce(() => {
    if (createServiceSuccess) {
      dispatch(getServiceNameSuccess([createServiceData]));
      dispatch(clearServiceData());
    } else if (!isUpdateServiceSuccess && !cloneServiceName?.length) {
      dispatch(getServiceRequest(serviceNameFromURL, version));
    } else if (cloneServiceName?.length) {
      dispatch(getServiceRequest(cloneServiceName, registryVersion));
    }
  }, DEBOUNCE_DELAY);

  /**
   * When View/Edit Service clicked, add service data to the store
   * @param activeService current service
   */
  const addServiceDetailsToStore = async (activeService: IService) => {
    if (activeService) {
      let serviceDetailsToStore: IServiceDetailsUIModel;
      if (activeService.soapAttributes?.envelopeNamespaces) {
        setIsDecryptionLoading(true);
        const data = await getDecryptedData(activeService);
        serviceDetailsToStore = constructServiceDetailsForStore(activeService, data);
        setIsDecryptionLoading(false);
      } else {
        serviceDetailsToStore = constructServiceDetailsForStore(activeService);
      }
      setServiceVersion(activeService?.version);
      // add service details to store
      if (cloneServiceName?.length) serviceDetailsToStore.serviceName = `${cloneConstant.CLONE_TXT}-${cloneServiceName}`;
      dispatch(addServiceDetails(serviceDetailsToStore));
      dispatch(addSaveAsDraft({ serviceData: serviceDetailsToStore, serviceOperations: [], ingressEvent: [], egressEvent: [] }));
      dispatch(serviceDetailsComplete());
      // add exceptions to store
      dispatch(clearAllExceptions());
      const exceptionForUI = getExceptionsForUI(activeService?.serviceExceptions);
      dispatch(addAllException(exceptionForUI));
      dispatch(addAllExceptionForDraft(exceptionForUI));
      // clear service ingress/egress events
      dispatch(clearEgressEventMapping());
      dispatch(clearIngressEventMapping());
      dispatch(clearServiceOperations());
      // add service operations to store
      const serviceOperationsForUI = getServiceOperationListForUI(activeService?.serviceOperations);
      dispatch(addAllServiceOperations(serviceOperationsForUI));
      dispatch(addServiceOperationForDraft(serviceOperationsForUI));
      // add ingress/egress events to reducer
      activeService.serviceOperations?.forEach((serviceOperation: IServiceOperationRequestModel) => {
        if (serviceOperation?.ingressEvents?.length) {
          const ingressEventList = getIngressEventListForUI(serviceOperation.ingressEvents, serviceOperation.serviceOperation);
          dispatch(addAllIngressEventForDraft(ingressEventList));
          dispatch(addIngressEventMappingList({ ingressEventMappingList: ingressEventList }));
          serviceOperation?.ingressEvents.forEach((ingressEventList) => {
            if (ingressEventList?.egressEvents?.length) {
              const egressEventList = getEgressEventListForUI(ingressEventList?.egressEvents, serviceOperation.serviceOperation, ingressEventList?.ingressEventName, ingressEventList?.ingressEventVersion);
              dispatch(addAllEgressEventForDraft(egressEventList));
              dispatch(addEgressEventMappingList({ egressEventMappingList: egressEventList }));
            }
          });
        }
      });
    }
  };

  const setDataWithDecryption = async () => {
    if (((version?.length && serviceDetails?.length) || cloneServiceName?.length) && (!isLoading)) {
      if (!cloneServiceName?.length) setViewStatus(serviceMode === SERVICE_MODE.VIEW);
      const activeService = getActiveService();
      if (activeService) {
        addServiceDetailsToStore(activeService);
      }
    } else if (version?.length && serviceErrorMessage) {
      setShowErrorModal(true);
    }
  };

  const getActiveService = () => {
    let activeServiceList;
    if (cloneServiceName?.length) {
      activeServiceList = serviceDetails.filter((item: IService) => (item.serviceName === cloneServiceName));
    } else {
      activeServiceList = serviceDetails.filter((item: IService) => (item.serviceName === serviceNameFromURL));
    }

    return activeServiceList[0];
  };

  const onCommentDialogConfirm = (comment: string) => {
    setShowCommentDialog(false);
    updateServiceStatus({ status: STATUS_TYPES.REJECTED, comment });
  };

  const handleReturnButton = (value: boolean) => {
    setReturnedStatusOpen(value);
  };

  const closeCommentDialog = () => {
    setReturnedStatusOpen(false);
  };

  // To toggle cloned service dialog pop-up
  const closeClonedServiceDialog = () => {
    setClonedServiceDialog(false);
  };

  /**
   * Close the fetch service details fetch modal
   */
  const onModalClose = () => {
    setShowErrorModal(false);
    history(`${URL_PATH.SERVICE_HOME}`, { state: location.state });
  };

  const onDeleteSuccess = () => {
    callServiceDetailsApi();
  };

  useEffect(() => {
    const unloadCallback = (event: BeforeUnloadEvent) => {
      event.preventDefault();
    };

    if (location.state?.from && location.state?.from !== '') {
      setPrevUrl(location.state?.from);
    }
    if (serviceMode.toLocaleLowerCase() !== SERVICE_MODE.VIEW) {
      window.onbeforeunload = unloadCallback;
      window.addEventListener('beforeunload', unloadCallback);
    }
    return () => window.removeEventListener('beforeunload', unloadCallback);
  }, []);

  useEffect(() => {
    if (!serviceDetailsData.serviceName && !serviceNameFromURL) { history(`${URL_PATH.SERVICE_HOME}${URL_PATH.CREATE_SERVICE}`, { state: { ...location.state, cloneServiceName, version: registryVersion } }); }
    const windowScrollStickyHeader = () => {
      const documentHeight = document.getElementById('container')?.clientHeight;
      const windowHeight = window.innerHeight;
      const headerHeight = document.getElementById('header-container')?.clientHeight;
      const sticky = headerHeight ?? 0;
      if (window.scrollY > sticky && documentHeight && ((windowHeight - sticky - 10) < documentHeight)) {
        setIsSticky(true);
      } else if (window.scrollY <= sticky - 20) {
        setIsSticky(false);
      }
    };
    window.addEventListener('scroll', windowScrollStickyHeader);
    return (() => {
      window.removeEventListener('scroll', windowScrollStickyHeader);
    });
  }, []);

  useEffect(() => {
    const unloadCallbackRefresh = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      const jdContext = getJourneyDesignerReturnContext();
      if (jdContext) {
        clearJourneyDesignerReturnContext();
        const returnState: JourneyDesignerServiceReturnLocationState = {
          jdServiceReturn: true,
          jdResume: jdContext.serviceReturnResume ?? { kind: 'none' },
          jdResumeSidebar: jdContext.resumeSidebar ?? 'ALL_SERVICES',
        };
        history(buildJourneyDesignerFlowViewUrl(jdContext, 'jdReturn=1'), { state: returnState });
        return '';
      }
      const queryParam = location.state?.path;
      if (location.state?.from === URL_PATH.DASHBOARD) { history(`${URL_PATH.CONTROL_PLANE}`); } else history(`${URL_PATH.SERVICE_HOME}${queryParam}`, { state: queryParam });
      return '';
    };
    if (serviceMode.toLocaleLowerCase() !== SERVICE_MODE.VIEW) {
      window.addEventListener('popstate', unloadCallbackRefresh);
    }

    return () => window.removeEventListener('popstate', unloadCallbackRefresh);
  }, []);

  useEffect(() => {
    if (navBarServiceDetails.state === NavBarState.COMPLETE && navBarServiceOperation.state === NavBarState.COMPLETE) {
      setBtnInvalid(false);
    } else {
      setBtnInvalid(true);
    }
  }, [navBarServiceDetails, navBarServiceOperation, navBarServiceExceptions, serviceOperations]);

  useEffect(() => {
    if (
      serviceMode === SERVICE_MODE.EDIT
      && serviceNameFromURL?.length
      && !isUpdateServiceSuccess
      && !createServiceSuccess
    ) {
      setViewStatus(false);
      enableEditService();
    } else if ((serviceMode === SERVICE_MODE.VIEW || isUpdateServiceSuccess) && serviceNameFromURL?.length) {
      setIsEdit(false);
      setViewStatus(true);
      if (isUpdateServiceSuccess) {
        setIsUpdateServiceSuccess(false);
        const normalizedViewPath = [
          `${URL_PATH.SERVICE_HOME}${URL_PATH.VIEW_SERVICE}`,
          serviceNameFromURL,
          serviceData?.version || version,
          SERVICE_MODE.VIEW
        ].filter(Boolean).join('/');
        history(normalizedViewPath, { state: location.state });
        dispatch(clearUpdateServiceData());
      }
    }
  }, [viewStatus, isUpdateServiceSuccess]);

  useEffect(() => {
    if (createServiceSuccess) {
      setIsCreateClicked(true);
    } else if (createServiceErrorMessage) {
      setIsCreateClicked(false);
    }
  }, [createServiceSuccess, createServiceErrorMessage]);

  useEffect(() => {
    setDataWithDecryption();
  }, [serviceDetails, isLoading, serviceErrorMessage]);

  useEffect(() => {
    const urlList = [servicePath.createServiceDetails, servicePath.exception, servicePath.serviceOperation];
    if (!serviceNameFromURL && urlList.includes(location.pathname)) {
      history(URL_PATH.CREATE_SERVICE, { state: { ...location.state, cloneServiceName, version: registryVersion } });
    } else if (serviceNameFromURL?.length && (!serviceDetails?.length || serviceErrorMessage)) {
      callServiceDetailsApi();
    }
  }, [serviceNameFromURL, version, serviceMode]);

  useEffect(() => {
    // if edit button is clicked, compares the changes and enable/disables the save as draft button
    if (isEdit || cloneServiceName?.length) {
      // get the data from api
      const clonedSaveAsDraftData: ISaveAsDraftInitialState = getClonedDraftServiceDetails(
        serviceDetailsData,
        serviceOperations,
        exceptions,
        ingressEventMappingList,
        egressEventMappingList
      );
      // get the data from UI
      const newDraftData = getClonedDraftServiceDetails(
        serviceDraftData.serviceData,
        [...serviceDraftData.serviceOperations],
        serviceDraftData.serviceExceptions
          ? [...serviceDraftData.serviceExceptions]
          : [],
        [...serviceDraftData.ingressEvent],
        [...serviceDraftData.egressEvent]
      );
      if (isEqual(clonedSaveAsDraftData, newDraftData)) {
        setBtnDraftEnabled(true);
        return;
      }
      setBtnDraftEnabled(false);
    }
  }, [
    serviceDetailsData,
    exceptions,
    serviceOperations,
    ingressEventMappingList,
    egressEventMappingList,
    isEdit
  ]);

  useEffect(() => {
    if (serviceUrlStatus.toLowerCase() === STATUS_TYPES.PENDING_APPROVAL.toLowerCase() && !(serviceDetailsData.authDetails?.authType) && serviceDetailsData.serviceName && viewStatus) {
      const serviceType = serviceDetailsData.serviceType.toUpperCase();
      if (serviceType === SERVICE_TYPE_KEY.DB2 || serviceType === SERVICE_TYPE_KEY.MQ || serviceType === SERVICE_TYPE_KEY.REST) {
        warning(
          translate('toast_component.lbl_warning'),
          translate('service_wrapper_component.msg_auth_details_not_present_body'),
          50000
        );
      }
    }
  }, [serviceDetailsData]);

  /**
  * Handles path modification, if any incorrect url path for view/edit service, redirects to service home screen
  */
  useEffect(() => {
    const pathArray = pathname.split('/');
    const modes = [SERVICE_MODE.EDIT, SERVICE_MODE.VIEW];
    const servicePageName = pathArray[3];
    // create service mode
    if (pathArray.length === 4) {
      return;
    }
    // View/Edit Service mode
    if (version) {
      const shouldRedirect = pathArray.length !== 7
        || !modes.includes(pathArray[6].toLocaleLowerCase())
        || !VIEW_SERVICE_URL_PATH.includes(servicePageName);

      if (shouldRedirect) {
        history(URL_PATH.SERVICE_HOME, { state: location.state });
      }
    } else if (!version || pathArray.slice(1, pathArray.length).includes('')) {
      history(URL_PATH.SERVICE_HOME, { state: location.state });
    }
  }, [version, pathname]);

  const showServiceNameExistWarning = (showServiceExistWarning: boolean) => {
    setIsServiceNameExistWarning(showServiceExistWarning);
  };

  const viewRoutes: RouteObject[] = [
    {
      path: servicePath.viewServiceDetails,
      element: <ServiceDetailsForm
        isEdit={isEdit}
        viewMode={viewStatus}
        ref={serviceNameFieldRef}
        showServiceNameExistWarning={showServiceNameExistWarning}
      />,
      requiredPermissions: [requiredAuthPermission.Services.view, requiredAuthPermission.Services.edit, requiredAuthPermission.Services.approve]
    },
    {
      path: servicePath.viewException,
      element: <ServiceExceptions
        serviceName={serviceNameFromURL}
        isExceptionDisabled={setExceptionInvalid}
      />,
      requiredPermissions: [requiredAuthPermission.Services.view, requiredAuthPermission.Services.edit, requiredAuthPermission.Services.approve]
    },
    {
      path: servicePath.viewServiceOperation,
      element: <ServiceOperationWrapper onServiceOperationEntered={() => { }} viewMode={viewStatus} changeView={updateViewMode} isEditService={isEdit} />,
      requiredPermissions: [requiredAuthPermission.Services.view, requiredAuthPermission.Services.edit, requiredAuthPermission.Services.approve]
    },
  ];

  const createRoutes: RouteObject[] = [
    {
      path: servicePath.createServiceDetails,
      element: <ServiceDetailsForm
        isEdit={isEdit}
        viewMode={viewStatus}
        ref={serviceNameFieldRef}
        showServiceNameExistWarning={showServiceNameExistWarning}
      />,
      requiredPermissions: [requiredAuthPermission.Services.edit, requiredAuthPermission.Services.approve]
    },
    {
      path: servicePath.exception,
      element: <ServiceExceptions
        serviceName={serviceNameFromURL}
        isExceptionDisabled={setExceptionInvalid}
      />,
      requiredPermissions: [requiredAuthPermission.Services.edit, requiredAuthPermission.Services.approve]
    },
    {
      path: servicePath.serviceOperation,
      element: <ServiceOperationWrapper onServiceOperationEntered={() => { }} viewMode={viewStatus} changeView={updateViewMode} />,
      requiredPermissions: [requiredAuthPermission.Services.edit, requiredAuthPermission.Services.approve]
    },
  ];

  useEffect(() => {
    if (cloneServiceName) {
      setIsEdit(false);
      setViewStatus(false);
      callServiceDetailsApi();
      setClonedServiceDialog(true);
    }
  }, [cloneServiceName]);

  /**
   * Calls on highlight link click
   */
  const onHighlightFieldClick = () => {
    serviceNameFieldRef?.current?.onHighlightFieldClick();
  };

  const updateService = () => {
    if (serviceUrlStatus === STATUS_TYPES.INACTIVE) {
      setIsPatchUpdateCalled(true);
      updateServiceStatus({ status: STATUS_TYPES.DRAFT });
    } else {
      updateServiceToDraftState(serviceUrlStatus);
    }
  };

  /**
   * Displays the service exist warning
   * @returns ui for warning
   */
  const showServiceExistWarning = () => (
    <div className={classes['service-exist-warning']}>
      <div className={classes['error-pad']} />
      <InfoMessage message="info_message.msg_service_exist" onHighlightFieldClick={onHighlightFieldClick} />
    </div>
  );

  const onDependencyGraphFound = (isFound: boolean, registryName: string, entityVersion: string, entityStatus: string) => {
    dispatchDependencyState({ type: DEPENDENCY_ACTIONS.DEPENDENCY_GRAPH_FOUND, addDependencyGraphPayload: { registryName, entityVersion, entityStatus, isDependencyGraphFound: isFound } });
  };

  const onCloseWarningClick = () => {
    dispatchDependencyState({ type: DEPENDENCY_ACTIONS.CLEAR_DEPENDENCY_GRAPH });
  };

  const navigateToServiceDependency = () => {
    history(
      `${URL_PATH.SERVICE_HOME}${URL_PATH.SERVICE_DEPENDENCY}`,
      { state: { ...location.state, associationServiceName: dependencyState.registryName, associationVersion: dependencyState.entityVersion, associationStatus: dependencyState.entityStatus } }
    );
  };

  const showDependencyGraphWarning = () => {
    if (dependencyState.isDependencyGraphFound) {
      return (
        <div className={`${classes['service-exist-warning']} ${classes['service-exist-error']}`}>
          <div className={classes['error-pad']} />
          <WarningMessage entityName={dependencyState.registryName} onHighlightFieldClick={navigateToServiceDependency} onCloseWarningClick={onCloseWarningClick} />
        </div>
      );
    }
    return null;
  };

  return (
    <div className={classes['service-wrapper']} id="container">
      <Modal
        modalShow={showErrorModal}
        modalTitle={translate('service_wrapper_component.lbl_confirm_error_title')}
        modalBody={translate('service_wrapper_component.msg_api_error')}
        btnSaveText={String(translate('service_wrapper_component.lbl_close'))}
        onSaveModal={onModalClose}
        onCloseModal={onModalClose}
        alignToCentre
        data-testid="activeStatusEditWarningModalNodeConfirm"
        disableCloseBtn={false}
        disableSaveBtn={false}
        type="warning"
      />
      <BreadcrumbComponent fieldName={serviceNameFromURL} isPreview={viewStatus} />
      <Loader show={isCreateServiceLoading || isLoading || isDecryptionLoading || isUpdateServiceLoading || isServicePatchLoading || importState.importSwaggerLoading} />
      {(shouldShowNavigationAwayPopup(serviceMode, isUpdateServiceSuccess, isActiveUpdate, isCreateClicked, isPatchUpdateCalled)) && <ConfirmationPopup urlPathArray={getExceptionalUrlPath()} />}
      <CommentDialog
        label={translate('events_wrapper.msg_reason_for_rejection')}
        subLabel={translate('comment_dialog.comment_service_form_text')}
        show={showCommentDialog}
        onHide={() => {
          setShowCommentDialog(false);
        }}
        onConfirm={(comment: string) => { onCommentDialogConfirm(comment); }}
      />
      {
        importedInvalidExceptions.length > 0
        && (<Modal
          modalTitle={String(translate('serviceWrapper.modalTitleReturn'))}
          modalBody={<InvalidExceptionModal exceptionList={importedInvalidExceptions} />}
          modalShow={importedInvalidExceptions.length > 0}
          onCloseModal={() => dispatch(clearInvalidExceptions())}
          dataTestId="import-swagger-exception"
          disableCloseBtn={false}
          alignToCentre
          type="warning"
        />)
      }
      {renderToast()}

      <Row className={`${classes['service-header-container']} ${getStickyStyle(isSticky, classes['sticky-service-header-container'], '')}`}>
        <Col md={3}>
          <div className={classes['service-title']} id="header-container">
            <CIPageHeader header={serviceNameFromURL && currentActiveService ? `${serviceNameFromURL}` : translate(getCreateViewServiceTitle(serviceMode, isEdit))} className={`${getStickyStyle(isSticky, classes['sticky-service-title'], '')}`} />
            {currentActiveService && !cloneServiceName.length && <div className={classes.status}><CIStatusCapsule status={currentActiveService?.status ?? ''} /></div>}
          </div>
          <p className={`${getStickyStyle(isSticky, classes['sticky-version-text'], classes['version-text'])}`}>
            {((isViewOrEditService) && serviceVersion) ? `Ver ${serviceVersion}` : ''}
          </p>
        </Col>
        <Col md={3} />
        <Col md={6}>
          <span className={`${getStickyStyle(isSticky, classes['sticky-service-header'], classes['service-header'])} ci-button-group`}>
            {!isViewOrEditService ? (
              <>
                <Button className={classes['btn-cancel']} onClick={backToServiceBtnHandler} variant="secondary" data-testid="btn-wrapper-cancel">
                  {translate('service_wrapper_component.lbl_cancel')}
                </Button>
                <Button
                  className={classes['btn-add']}
                  onClick={callCreateServiceApi}
                  variant="primary"
                  disabled={btnInvalid}
                  data-testid="btn-create"
                >
                  {translate('service_wrapper_component.lbl_create')}
                </Button>
              </>
            ) : (
              <ServicePatchButtons
                handleReturnButton={handleReturnButton}
                isEditService={isEdit}
                status={serviceUrlStatus}
                btnInvalid={btnDraftEnabled || btnInvalid || exceptionInvalid}
                backToServiceBtnHandler={backToServiceBtnHandler}
                callUpdateServiceStatusApi={(status: string) => { updateServiceStatus({ status }); }}
                enableEditService={enableEditService}
                updateServiceToDraftState={updateService}
                onRejectClick={() => setShowCommentDialog(true)}
                serviceCommentHistoryCheck={serviceCommentHistoryCheck}
                onDeleteSuccess={onDeleteSuccess}
                onDependencyGraphFound={onDependencyGraphFound}
              />
            )}
          </span>
        </Col>
      </Row>
      {isServiceNameExistWarning ? showServiceExistWarning() : null}
      {showDependencyGraphWarning()}
      <Row className={returnedStatusOpen ? classes['tabs-with-modal-open-row'] : ''}>

        <Col md={2} className={classes['tabs-with-modal-open']}>
          <CreateServiceSideNav viewStatus={isViewOrEditService} serviceNameFromURL={serviceNameFromURL} updateFileData={updateImportFileData} returnedStatusOpen={returnedStatusOpen} isEdit={isEdit} />
        </Col>
        <Col md={10} className={classes['service-body']}>
          {showImportNotification(importState, closeUploadNotification)}
          {clonedServiceDialog && (

            <div className={classes['clone-service-wrapper']}>
              <div className={classes['clone-service-wrapper-content']}>
                <MyIcon name="infoFillIcon" className={classes['info-icon']} /><span className={classes['font-bold']}>{translate('serviceWrapper.clone_service_text')}</span> {translate('serviceWrapper.clone_service_dialog')}
              </div>
              <Button
                variant="tertiary"
                className={classes['cancel-icon']}
                onClick={closeClonedServiceDialog}
                data-testid="cancel-clone-popup"
                aria-label="cancel-clone-popup"
              >

                <MyIcon name="xMarkSmallIcon" />
              </Button>
            </div>
          )}
          <CIRouteguard routes={isViewOrEditService ? viewRoutes : createRoutes} userPermissions={permissionObject.permission} />
        </Col>
      </Row>
      {returnedStatusOpen
        ? <SideModal title={String(translate('serviceWrapper.modalTitleReturn'))} onClose={closeCommentDialog} breakPoint="xs" lined align="right" className={classes['modal-top-alignment']} children={<CommentHistoryModal eventDetailsForHistory={serviceCommentHistoryCheck} />} /> : ''}
    </div>
  );
};

export default ServiceWrapper;
